import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  Link,
  Text,
  Icon,
  Tooltip,
} from '@chakra-ui/react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Extend window object to include left sidebar update functions
declare global {
  interface Window {
    updateLeftSidebar?: (data: {
      section: string;
      subsections: Array<{
        path: string;
        label: string;
        icon?: React.ComponentType;
      }>;
    }) => void;
    updateTeamSections?: (sections: Array<{
      path?: string;
      label: string;
      icon?: React.ComponentType;
      onClick?: () => void;
      isActive?: boolean;
      count?: number;
    }>) => void;
  }
}

// Simple icon components
const ProfileIcon = () => (
  <Icon viewBox="0 0 24 24" boxSize={5}>
    <path
      fill="currentColor"
      d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"
    />
  </Icon>
);

const HomeIcon = () => (
  <Icon viewBox="0 0 24 24" boxSize={5}>
    <path
      fill="currentColor"
      d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"
    />
  </Icon>
);

const TeamsIcon = () => (
  <Icon viewBox="0 0 24 24" boxSize={5}>
    <path
      fill="currentColor"
      d="M16 4c0-1.11.89-2 2-2s2 .89 2 2-.89 2-2 2-2-.89-2-2zm4 18v-6h2.5l-2.54-7.63A1.002 1.002 0 0 0 19 8h-2c-.55 0-1 .45-1 1v13h4zm-12.5 0v-6.5H9V9c0-.55-.45-1-1-1H6c-.55 0-1 .45-1 1v13h2.5zm3-13c1.11 0 2-.89 2-2s-.89-2-2-2-2 .89-2 2 .89 2 2 2z"
    />
  </Icon>
);

const BalanceIcon = () => (
  <Icon viewBox="0 0 24 24" boxSize={5}>
    <path
      fill="currentColor"
      d="M11.8 10.9c-2.27-.59-3-1.2-3-2.15 0-1.09 1.01-1.85 2.7-1.85 1.78 0 2.44.85 2.5 2.1h2.21c-.07-1.72-1.12-3.3-3.21-3.81V3h-3v2.16c-1.94.42-3.5 1.68-3.5 3.61 0 2.31 1.91 3.46 4.7 4.13 2.5.6 3 1.48 3 2.41 0 .69-.49 1.79-2.7 1.79-2.06 0-2.87-.92-2.98-2.1h-2.2c.12 2.19 1.76 3.42 3.68 3.83V21h3v-2.15c1.95-.37 3.5-1.5 3.5-3.55 0-2.84-2.43-3.81-4.7-4.4z"
    />
  </Icon>
);

const SettingsIcon = () => (
  <Icon viewBox="0 0 24 24" boxSize={5}>
    <path
      fill="currentColor"
      d="M19.14,12.94c0.04-0.3,0.06-0.61,0.06-0.94c0-0.32-0.02-0.64-0.07-0.94l2.03-1.58c0.18-0.14,0.23-0.41,0.12-0.61 l-1.92-3.32c-0.12-0.22-0.37-0.29-0.59-0.22l-2.39,0.96c-0.5-0.38-1.03-0.7-1.62-0.94L14.4,2.81c-0.04-0.24-0.24-0.41-0.48-0.41 h-3.84c-0.24,0-0.43,0.17-0.47,0.41L9.25,5.35C8.66,5.59,8.12,5.92,7.63,6.29L5.24,5.33c-0.22-0.08-0.47,0-0.59,0.22L2.74,8.87 C2.62,9.08,2.66,9.34,2.86,9.48l2.03,1.58C4.84,11.36,4.8,11.69,4.8,12s0.02,0.64,0.07,0.94l-2.03,1.58 c-0.18,0.14-0.23,0.41-0.12,0.61l1.92,3.32c0.12,0.22,0.37,0.29,0.59,0.22l2.39-0.96c0.5,0.38,1.03,0.7,1.62,0.94l0.36,2.54 c0.05,0.24,0.24,0.41,0.48,0.41h3.84c0.24,0,0.44-0.17,0.47-0.41l0.36-2.54c0.59-0.24,1.13-0.56,1.62-0.94l2.39,0.96 c0.22,0.08,0.47,0,0.59-0.22l1.92-3.32c0.12-0.22,0.07-0.47-0.12-0.61L19.14,12.94z M12,15.6c-1.98,0-3.6-1.62-3.6-3.6 s1.62-3.6,3.6-3.6s3.6,1.62,3.6,3.6S13.98,15.6,12,15.6z"
    />
  </Icon>
);

const SearchIcon = () => (
  <Icon viewBox="0 0 24 24" boxSize={5}>
    <path
      fill="currentColor"
      d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"
    />
  </Icon>
);

const NotificationIcon = () => (
  <Icon viewBox="0 0 24 24" boxSize={5}>
    <path
      fill="currentColor"
      d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6v-5c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z"
    />
  </Icon>
);

const MachinesIcon = () => (
  <Icon viewBox="0 0 24 24" boxSize={5}>
    <path
      fill="currentColor"
      d="M20 6h-2.18c.11-.31.18-.65.18-1a2.996 2.996 0 0 0-5.5-1.65l-.5.67-.5-.68C10.96 2.54 10.05 2 9 2 7.34 2 6 3.34 6 5c0 .35.07.69.18 1H4c-1.11 0-1.99.89-1.99 2L2 19c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V8c0-1.11-.89-2-2-2zm-5-2c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zM9 4c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1z"
    />
  </Icon>
);

const PlayIcon = () => (
  <Icon viewBox="0 0 24 24" boxSize={5}>
    <path
      fill="currentColor"
      d="M21 6H3c-1.1 0-2 .9-2 2v8c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zM11 13H8v3H6v-3H3v-2h3V8h2v3h3v2zm4.5 2a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3zm4-3a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3z"
    />
  </Icon>
);

const VoiceIcon = () => (
  <Icon viewBox="0 0 24 24" boxSize={5}>
    <path
      fill="currentColor"
      d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 9c0-1.1.9-2 2-2s2 .9 2 2-.9 2-2 2-2-.9-2-2zm4 6h-4v-.5c0-1 1.2-1.5 2-1.5s2 .5 2 1.5V17z"
    />
  </Icon>
);

// Team section icons
const OverviewIcon = () => (
  <Icon viewBox="0 0 24 24" boxSize={4}>
    <path
      fill="currentColor"
      d="M19 3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.89-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"
    />
  </Icon>
);

const MembersIcon = () => (
  <Icon viewBox="0 0 24 24" boxSize={4}>
    <path
      fill="currentColor"
      d="M16 4c0-1.11.89-2 2-2s2 .89 2 2-.89 2-2 2-2-.89-2-2zM4 18v-6c0-1.1.9-2 2-2s2 .9 2 2v6h8v-7.5c0-.83-.67-1.5-1.5-1.5S13 9.67 13 10.5V11H9V8.5C9 7.12 10.12 6 11.5 6S14 7.12 14 8.5V9h1v1.5c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5V9c0-2.21-1.79-4-4-4-1.11 0-2.11.45-2.83 1.17C10.45 5.89 9.45 5.44 8.34 5.44 6.13 5.44 4.34 7.23 4.34 9.44V18z"
    />
  </Icon>
);

const InvitationsIcon = () => (
  <Icon viewBox="0 0 24 24" boxSize={4}>
    <path
      fill="currentColor"
      d="M20 6l-2.83-2.83c-.78-.78-2.05-.78-2.83 0L12 5.51 9.66 3.17c-.78-.78-2.05-.78-2.83 0L4 6l2.51 2.51L8.83 10 4 14.83 6.83 17.66 11.66 12.83 14.17 15.34 17 12.51 20 6z"
    />
  </Icon>
);

const RolesIcon = () => (
  <Icon viewBox="0 0 24 24" boxSize={4}>
    <path
      fill="currentColor"
      d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
    />
  </Icon>
);

const RequestsIcon = () => (
  <Icon viewBox="0 0 24 24" boxSize={4}>
    <path
      fill="currentColor"
      d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"
    />
  </Icon>
);

const ContactsIcon = () => (
  <Icon viewBox="0 0 24 24" boxSize={4}>
    <path
      fill="currentColor"
      d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"
    />
  </Icon>
);

const AuditLogsIcon = () => (
  <Icon viewBox="0 0 24 24" boxSize={4}>
    <path
      fill="currentColor"
      d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"
    />
  </Icon>
);

const TeamSettingsIcon = () => (
  <Icon viewBox="0 0 24 24" boxSize={4}>
    <path
      fill="currentColor"
      d="M12,15.5A3.5,3.5 0 0,1 8.5,12A3.5,3.5 0 0,1 12,8.5a3.5,3.5 0 0,1 3.5,3.5a3.5,3.5 0 0,1 -3.5,3.5M19.43,12.97C19.47,12.65 19.5,12.33 19.5,12C19.5,11.67 19.47,11.34 19.43,11L21.54,9.37C21.73,9.22 21.78,8.95 21.66,8.73L19.66,5.27C19.54,5.05 19.27,4.96 19.05,5.05L16.56,6.05C16.04,5.66 15.5,5.32 14.87,5.07L14.5,2.42C14.46,2.18 14.25,2 14,2H10C9.75,2 9.54,2.18 9.5,2.42L9.13,5.07C8.5,5.32 7.96,5.66 7.44,6.05L4.95,5.05C4.73,4.96 4.46,5.05 4.34,5.27L2.34,8.73C2.22,8.95 2.27,9.22 2.46,9.37L4.57,11C4.53,11.34 4.5,11.67 4.5,12C4.5,12.33 4.53,12.65 4.57,12.97L2.46,14.63C2.27,14.78 2.22,15.05 2.34,15.27L4.34,18.73C4.46,18.95 4.73,19.03 4.95,18.95L7.44,17.94C7.96,18.34 8.5,18.68 9.13,18.93L9.5,21.58C9.54,21.82 9.75,22 10,22H14C14.25,22 14.46,21.82 14.5,21.58L14.87,18.93C15.5,18.68 16.04,18.34 16.56,17.94L19.05,18.95C19.27,19.03 19.54,18.95 19.66,18.73L21.66,15.27C21.78,15.05 21.73,14.78 21.54,14.63L19.43,12.97Z"
    />
  </Icon>
);

interface LeftSidebarProps {
  isOpen: boolean;
  onCompactSecondLevelChange?: (compact: boolean) => void;
}

interface SubsectionItem {
  path: string;
  label: string;
  icon?: React.ComponentType;
}

interface TeamSectionItem {
  path?: string;
  label: string;
  icon?: React.ComponentType;
  onClick?: () => void;
  isActive?: boolean;
  count?: number;
}

export const LeftSidebar: React.FC<LeftSidebarProps> = ({ isOpen, onCompactSecondLevelChange }) => {
  const { t } = useTranslation();
  const location = useLocation();
  
  // State for subsections and team sections
  const [activeSection, setActiveSection] = useState<string>('');
  const [subsections, setSubsections] = useState<SubsectionItem[]>([]);
  const [teamSections, setTeamSections] = useState<TeamSectionItem[]>([]);
  const [compactMode, setCompactMode] = useState(false);
  const [compactSecondLevel, setCompactSecondLevel] = useState(false);

  // Global sidebar update functions
  useEffect(() => {
    window.updateLeftSidebar = (data: { section: string; subsections: SubsectionItem[] }) => {
      setActiveSection(data.section);
      setSubsections(data.subsections || []);
    };

    // New function for team sections (third level)
    window.updateTeamSections = (sections: TeamSectionItem[]) => {
      setTeamSections(sections);
      // Compact mode will be handled by useEffect based on all navigation levels
    };

    return () => {
      delete window.updateLeftSidebar;
      delete window.updateTeamSections;
    };
  }, []);

  // Clear subsections when navigating away from a section
  useEffect(() => {
    const currentPath = location.pathname;
    const isSubsectionOpen = subsections.length > 0;
    const isTeamPage = currentPath.startsWith('/teams/');
    const isTeamNavOpen = teamSections.length > 0;
    
    // Main menu should be compact if there are subsections open
    // Second level should be compact if there are team sections open
    const shouldCompactMain = isSubsectionOpen;
    const shouldCompactSecond = isTeamNavOpen;

    if (activeSection && !currentPath.startsWith(`/${activeSection}`)) {
      setActiveSection('');
      setSubsections([]);
      setTeamSections([]);
      setCompactMode(false);
    } else {
      // Set compact mode for main menu and second level
      setCompactMode(shouldCompactMain);
      setCompactSecondLevel(shouldCompactSecond);
      onCompactSecondLevelChange?.(shouldCompactSecond);
    }
  }, [location.pathname, activeSection, subsections.length, teamSections.length]);

  // Settings section icons
  const AccountIcon = SettingsIcon;
  const ProfileSettingsIcon = ProfileIcon;
  const PrivacyIcon = AuditLogsIcon;
  const UIIcon = MachinesIcon;
  const NotificationSettingsIcon = NotificationIcon;

  // Settings subsections
  const settingsSubsections = [
    {
      path: '/settings/account',
      label: t('settings.sections.account'),
      icon: AccountIcon,
    },
    {
      path: '/settings/profile',
      label: t('settings.sections.profile'),
      icon: ProfileSettingsIcon,
    },
    {
      path: '/settings/privacy',
      label: t('settings.sections.privacy'),
      icon: PrivacyIcon,
    },
    {
      path: '/settings/ui',
      label: t('settings.sections.ui'),
      icon: UIIcon,
    },
    {
      path: '/settings/notifications',
      label: t('settings.sections.notifications'),
      icon: NotificationSettingsIcon,
    },
  ];

  // Автоматически активируем подпункты для /settings
  useEffect(() => {
    if (location.pathname.startsWith('/settings')) {
      setActiveSection('settings');
      setSubsections(settingsSubsections);
    }
  }, [location.pathname]);

  // Theme
  const bgColor = 'bg.surface';
  const borderColor = 'border.default';
  const textSecondary = 'text.secondary';
  const textPrimary = 'text.primary';

  const navigationItems = [
    {
      path: '/play',
      label: t('nav.play'),
      icon: PlayIcon,
    },
    {
      path: '/profile',
      label: t('nav.profile'),
      icon: ProfileIcon,
    },
    { 
      path: '/profiles',
      label: t('nav.search'),
      icon: SearchIcon,
    },
    {
      path: '/teams',
      label: t('nav.teams'),
      icon: TeamsIcon,
    },
    {
      path: '/balance',
      label: t('nav.balance'),
      icon: BalanceIcon,
    },
    {
      path: '/voice',
      label: t('nav.voice'),
      icon: VoiceIcon,
    },
    {
      path: '/notifications',
      label: t('nav.notifications'),
      icon: NotificationIcon,
    },
    {
      path: '/settings',
      label: t('nav.settings'),
      icon: SettingsIcon,
    },
    {
      path: '/machine',
      label: t('nav.machines'),
      icon: MachinesIcon,
    },
  ];

  if (!isOpen) return null;

  // Check if we're showing subsections
  const showingSubsections = activeSection && subsections.length > 0;
  const showingTeamSections = teamSections.length > 0;

  // Compact mode dimensions
  const mainWidth = compactMode ? "60px" : "250px";
  const secondWidth = compactSecondLevel ? "60px" : "200px";
  const teamWidth = "200px";

  return (
    <>
      {/* Main left sidebar */}
      <Box
        position="fixed"
        left={0}
        top="60px"
        width={mainWidth}
        height="calc(100vh - 60px)"
        bg={bgColor}
        borderRight="1px"
        borderColor={borderColor}
        zIndex={999}
        p={compactMode ? 2 : 4}
        transition="width 0.3s ease-in-out"
      >
        <VStack spacing={2} align="stretch">
          {navigationItems.map((item) => {
            const isActive = location.pathname.startsWith(item.path) || location.pathname === item.path;
            
            const linkContent = (
              <Link
                key={item.path}
                as={RouterLink}
                to={item.path}
                display="flex"
                alignItems="center"
                justifyContent={compactMode ? "center" : "flex-start"}
                px={compactMode ? 2 : 3}
                py={2}
                borderRadius="md"
                bg={isActive ? 'primary.50' : 'transparent'}
                color={isActive ? 'primary.600' : textPrimary}
                _hover={{
                  bg: isActive ? 'primary.100' : 'bg.subtle',
                  textDecoration: 'none',
                }}
                _dark={{
                  bg: isActive ? 'primary.900' : 'transparent',
                  color: isActive ? 'primary.200' : textPrimary,
                  _hover: {
                    bg: isActive ? 'primary.800' : 'bg.subtle',
                  },
                }}
              >
                <item.icon />
                {!compactMode && (
                  <Text ml={3} fontSize="sm" fontWeight={isActive ? 'semibold' : 'normal'}>
                    {item.label}
                  </Text>
                )}
              </Link>
            );

            if (compactMode) {
              return (
                <Tooltip key={item.path} label={item.label} placement="right" hasArrow>
                  {linkContent}
                </Tooltip>
              );
            }

            return linkContent;
          })}
        </VStack>
      </Box>

      {/* Secondary left sidebar for subsections */}
      {showingSubsections && (
        <Box
          position="fixed"
          left={mainWidth}
          top="60px"
          width={secondWidth}
          height="calc(100vh - 60px)"
          bg={bgColor}
          borderRight="1px"
          borderColor={borderColor}
          zIndex={998}
          p={compactSecondLevel ? 2 : 4}
          transform="translateX(0)"
          transition="all 0.3s ease-in-out"
        >
          <VStack spacing={2} align="stretch">
            {!compactSecondLevel && (
              <Text fontSize="xs" fontWeight="bold" color={textSecondary} mb={2}>
                {t(`nav.${activeSection}`)}
              </Text>
            )}
            {subsections.map((subsection) => {
              const isActive = location.pathname === subsection.path;
              
              const linkContent = (
                <Link
                  key={subsection.path}
                  as={RouterLink}
                  to={subsection.path}
                  display="flex"
                  alignItems="center"
                  justifyContent={compactSecondLevel ? "center" : "flex-start"}
                  px={compactSecondLevel ? 2 : 3}
                  py={2}
                  borderRadius="md"
                  bg={isActive ? 'primary.50' : 'transparent'}
                  color={isActive ? 'primary.600' : textPrimary}
                  _hover={{
                    bg: isActive ? 'primary.100' : 'bg.subtle',
                    textDecoration: 'none',
                  }}
                  _dark={{
                    bg: isActive ? 'primary.900' : 'transparent',
                    color: isActive ? 'primary.200' : textPrimary,
                    _hover: {
                      bg: isActive ? 'primary.800' : 'bg.subtle',
                    },
                  }}
                >
                  {subsection.icon && <subsection.icon />}
                  {!compactSecondLevel && (
                    <Text ml={subsection.icon ? 3 : 0} fontSize="sm" fontWeight={isActive ? 'semibold' : 'normal'}>
                      {subsection.label}
                    </Text>
                  )}
                </Link>
              );

              if (compactSecondLevel) {
                return (
                  <Tooltip key={subsection.path} label={subsection.label} placement="right" hasArrow>
                    {linkContent}
                  </Tooltip>
                );
              }

              return linkContent;
            })}
          </VStack>
        </Box>
      )}

      {/* Third level sidebar for team sections */}
      {teamSections.length > 0 && (
        <Box
          position="fixed"
          left={showingSubsections ? `calc(${mainWidth} + ${secondWidth})` : mainWidth}
          top="60px"
          width={teamWidth}
          height="calc(100vh - 60px)"
          bg={bgColor}
          borderRight="1px"
          borderColor={borderColor}
          zIndex={997}
          p={4}
          transform="translateX(0)"
          transition="all 0.3s ease-in-out"
        >
          <VStack spacing={2} align="stretch">
            <Text fontSize="xs" fontWeight="bold" color={textSecondary} mb={2}>
              {t('teams.teamNavigation')}
            </Text>
            {teamSections.map((section, index) => {
              const linkContent = (
                <Box
                  key={section.path || index}
                  display="flex"
                  alignItems="center"
                  px={3}
                  py={2}
                  borderRadius="md"
                  bg={section.isActive ? 'primary.50' : 'transparent'}
                  color={section.isActive ? 'primary.600' : textPrimary}
                  cursor="pointer"
                  _hover={{
                    bg: section.isActive ? 'primary.100' : 'bg.subtle',
                  }}
                  _dark={{
                    bg: section.isActive ? 'primary.900' : 'transparent',
                    color: section.isActive ? 'primary.200' : textPrimary,
                    _hover: {
                      bg: section.isActive ? 'primary.800' : 'bg.subtle',
                    },
                  }}
                  onClick={section.onClick}
                >
                  {section.icon && <section.icon />}
                  <Text ml={section.icon ? 3 : 0} fontSize="sm" fontWeight={section.isActive ? 'semibold' : 'normal'}>
                    {section.label}
                    {section.count !== undefined && section.count > 0 && (
                      <Text as="span" fontSize="xs" color={textSecondary} ml={1}>
                        ({section.count})
                      </Text>
                    )}
                  </Text>
                </Box>
              );

              return linkContent;
            })}
          </VStack>
        </Box>
      )}
    </>
  );
}; 